import { Link } from "@tanstack/react-router";
import { ArrowRight, CalendarClock, CheckCircle, Euro, Hammer, MapPin, Phone } from "lucide-react";

import { SectionHeading } from "./SectionHeading";
import { CtaBand } from "./CtaBand";
import { ServiceCard } from "./ServiceCard";
import { diensten } from "../lib/data";
import { locatieContent, nap, type LocatieContent } from "../lib/locatie-content";
import { SITE_URL, ogImageDefault, ogImageMeta } from "../lib/seo";

interface LocationPageProps {
  slug: string;
}

/** Plaatsnaam → routepad, zodat interne links type-safe blijven. */
const locatieLinks = {
  Dordrecht: "/timmerman-dordrecht",
  Zwijndrecht: "/timmerman-zwijndrecht",
  Papendrecht: "/timmerman-papendrecht",
  Sliedrecht: "/timmerman-sliedrecht",
} as const;

export function LocationPage({ slug }: LocationPageProps) {
  const content = locatieContent[slug];
  if (!content) return null;

  const featured = content.featuredDiensten
    .map((s) => diensten.find((d) => d.slug === s))
    .filter((d): d is (typeof diensten)[number] => Boolean(d));

  return (
    <>
      <div className="py-14 sm:py-20 lg:py-28">
        <div className="container-tight">
          <SectionHeading
            as="h1"
            eyebrow={`Timmerman ${content.plaats}`}
            title={`Timmerman in ${content.plaats} voor aanbouw, dakkapel, kozijnen en renovatie`}
            description={content.intro}
            align="center"
            className="mx-auto"
          />

          <div className="mx-auto mt-10 max-w-3xl space-y-6 sm:mt-14">
            {content.paragraphs.map((p) => (
              <p key={p.slice(0, 24)} className="text-base leading-relaxed text-muted-foreground">
                {p}
              </p>
            ))}

            {content.wijken && content.wijken.length > 0 && (
              <div className="rounded-2xl border border-border/60 bg-card p-6 sm:p-8">
                <h2 className="font-heading text-xl font-semibold text-foreground">
                  Wijken in {content.plaats} waar we werken
                </h2>
                <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {content.wijken.map((wijk) => (
                    <li key={wijk} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                      {wijk}
                    </li>
                  ))}
                </ul>
                {(content.postcodes || content.reistijd) && (
                  <p className="mt-4 text-sm text-muted-foreground">
                    {content.postcodes && <>Postcodegebied {content.postcodes}. </>}
                    {content.reistijd}
                  </p>
                )}
              </div>
            )}

            <div className="flex flex-col gap-3 pt-4 sm:flex-row">
              <Link
                to="/contact"
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Gratis offerte aanvragen
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                to="/diensten"
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-md border border-input bg-background px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-accent"
              >
                Bekijk alle diensten
              </Link>
            </div>

            <ul className="grid grid-cols-2 gap-x-4 gap-y-2 pt-4 text-sm text-muted-foreground sm:grid-cols-4">
              {["Ter plekke op maat gemaakt", "Gratis adviesgesprek", "7+ jaar ervaring", "Duidelijke offerte"].map(
                (item) => (
                  <li key={item} className="flex items-start gap-2">
                    <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ),
              )}
            </ul>
          </div>

          {/* Voorbeeldprojecten */}
          {content.projecten && content.projecten.length > 0 && (
            <div className="mt-20">
              <SectionHeading
                eyebrow="Voorbeeldprojecten"
                title={`Werk dat we uitvoerden in ${content.plaats}`}
                description="Een greep uit recente projecten, inclusief wijk en doorlooptijd."
                align="center"
              />
              <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {content.projecten.map((project) => (
                  <article
                    key={project.titel}
                    className="rounded-2xl border border-border/60 bg-card p-6 text-left"
                  >
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary">
                      <Hammer className="h-4 w-4" aria-hidden="true" />
                      {project.wijk}
                    </div>
                    <h3 className="mt-3 font-heading text-lg font-semibold text-foreground">{project.titel}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{project.beschrijving}</p>
                    <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                      <CalendarClock className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                      Doorlooptijd: {project.doorlooptijd}
                    </p>
                  </article>
                ))}
              </div>
              <p className="mt-6 text-center text-sm text-muted-foreground">
                Meer projecten zien?{" "}
                <Link to="/werk-in-beeld" className="font-semibold text-primary underline-offset-4 hover:underline">
                  Bekijk werk in beeld
                </Link>
                .
              </p>
            </div>
          )}

          {/* Planning en werkzaamheden */}
          {content.planning && content.planning.length > 0 && (
            <div className="mt-20">
              <SectionHeading
                eyebrow="Planning en werkzaamheden"
                title={`Zo pakken we een klus in ${content.plaats} aan`}
                align="center"
              />
              <ol className="mx-auto mt-10 max-w-3xl space-y-4">
                {content.planning.map((stap) => (
                  <li key={stap.titel} className="rounded-2xl border border-border/60 bg-card p-6">
                    <h3 className="font-heading text-base font-semibold text-foreground">{stap.titel}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{stap.beschrijving}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Lokale prijsindicatie */}
          {content.prijzen && content.prijzen.length > 0 && (
            <div className="mt-20">
              <SectionHeading
                eyebrow="Prijsindicatie"
                title={`Wat kost timmerwerk in ${content.plaats}?`}
                align="center"
              />
              <div className="mx-auto mt-10 max-w-3xl overflow-hidden rounded-2xl border border-border/60 bg-card">
                <table className="w-full text-left text-sm">
                  <thead className="bg-muted/60">
                    <tr>
                      <th scope="col" className="px-4 py-3 font-semibold text-foreground sm:px-6">
                        Werkzaamheden
                      </th>
                      <th scope="col" className="px-4 py-3 font-semibold text-foreground sm:px-6">
                        Indicatie {content.plaats}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {content.prijzen.map((prijs) => (
                      <tr key={prijs.dienst} className="border-t border-border/60">
                        <th scope="row" className="px-4 py-3 font-normal text-muted-foreground sm:px-6">
                          {prijs.href ? (
                            <Link to={prijs.href} className="text-primary underline-offset-4 hover:underline">
                              {prijs.dienst}
                            </Link>
                          ) : (
                            prijs.dienst
                          )}
                        </th>
                        <td className="px-4 py-3 font-semibold text-foreground sm:px-6">
                          <span className="inline-flex items-center gap-2">
                            <Euro className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                            {prijs.indicatie}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {content.prijsToelichting && (
                <p className="mx-auto mt-4 max-w-3xl text-sm text-muted-foreground">{content.prijsToelichting}</p>
              )}
              <p className="mx-auto mt-3 max-w-3xl text-sm text-muted-foreground">
                Bekijk ook het volledige{" "}
                <Link to="/kosten" className="font-semibold text-primary underline-offset-4 hover:underline">
                  prijzenoverzicht
                </Link>{" "}
                of vraag een{" "}
                <Link to="/contact" className="font-semibold text-primary underline-offset-4 hover:underline">
                  offerte op maat
                </Link>{" "}
                aan.
              </p>
            </div>
          )}

          {/* Diensten */}
          <div className="mt-20">
            <SectionHeading
              eyebrow="Diensten"
              title={`Waarmee kan Fedor Timmerwerken je helpen in ${content.plaats}?`}
              align="center"
            />
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {featured.map((dienst) => (
                <ServiceCard
                  key={dienst.slug}
                  title={dienst.title}
                  description={dienst.description}
                  imageSrc={dienst.imageSrc}
                  imageSrc768={dienst.imageSrc768}
                  imageAlt={dienst.imageAlt}
                  slug={dienst.slug}
                />
              ))}
            </div>
          </div>

          {/* NAP-blok + interne links */}
          <div className="mx-auto mt-20 grid max-w-3xl gap-6 sm:grid-cols-2">
            <div className="rounded-2xl border border-border/60 bg-card p-6">
              <h2 className="font-heading text-lg font-semibold text-foreground">
                {nap.naam} — bedrijfsgegevens
              </h2>
              <address className="mt-4 space-y-1 text-sm not-italic text-muted-foreground">
                <p>{nap.juridischeNaam}</p>
                <p>{nap.straat}</p>
                <p>
                  {nap.postcode} {nap.plaats}, {nap.landNaam}
                </p>
                <p>
                  <a href={`tel:${nap.telefoon}`} className="inline-flex items-center gap-2 text-primary hover:underline">
                    <Phone className="h-4 w-4" aria-hidden="true" />
                    {nap.telefoonWeergave}
                  </a>
                </p>
                <p>
                  <a href={`mailto:${nap.email}`} className="text-primary hover:underline">
                    {nap.email}
                  </a>
                </p>
                <p>KvK {nap.kvk}</p>
                <p>Openingstijden: {nap.openingstijden}</p>
              </address>
            </div>

            <div className="rounded-2xl border border-border/60 bg-card p-6">
              <h2 className="font-heading text-lg font-semibold text-foreground">Ook actief in de regio</h2>
              <ul className="mt-4 space-y-2 text-sm">
                {(Object.keys(locatieLinks) as Array<keyof typeof locatieLinks>)
                  .filter((plaats) => plaats !== content.plaats)
                  .map((plaats) => (
                    <li key={plaats}>
                      <Link
                        to={locatieLinks[plaats]}
                        className="inline-flex items-center gap-2 text-primary underline-offset-4 hover:underline"
                      >
                        <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
                        Timmerman {plaats}
                      </Link>
                    </li>
                  ))}
                <li>
                  <Link
                    to="/kozijnen-dordrecht"
                    className="inline-flex items-center gap-2 text-primary underline-offset-4 hover:underline"
                  >
                    <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
                    Kozijnen Dordrecht
                  </Link>
                </li>
                <li>
                  <Link
                    to="/dakkapel-dordrecht"
                    className="inline-flex items-center gap-2 text-primary underline-offset-4 hover:underline"
                  >
                    <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
                    Dakkapel Dordrecht
                  </Link>
                </li>
                <li>
                  <Link
                    to="/aanbouw-dordrecht"
                    className="inline-flex items-center gap-2 text-primary underline-offset-4 hover:underline"
                  >
                    <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
                    Aanbouw Dordrecht
                  </Link>
                </li>
                <li>
                  <Link
                    to="/kozijnen-zwijndrecht"
                    className="inline-flex items-center gap-2 text-primary underline-offset-4 hover:underline"
                  >
                    <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
                    Kozijnen Zwijndrecht
                  </Link>
                </li>
                <li>
                  <Link
                    to="/verbouwing-dordrecht"
                    className="inline-flex items-center gap-2 text-primary underline-offset-4 hover:underline"
                  >
                    <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
                    Verbouwing Dordrecht
                  </Link>
                </li>
                <li>
                  <Link
                    to="/schuur-tuinhuis-laten-bouwen"
                    className="inline-flex items-center gap-2 text-primary underline-offset-4 hover:underline"
                  >
                    <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
                    Schuur of tuinhuis laten bouwen
                  </Link>
                </li>

                <li>
                  <Link to="/over" className="text-primary underline-offset-4 hover:underline">
                    Over Fedor Timmerwerken
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-primary underline-offset-4 hover:underline">
                    Contact en adviesgesprek
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <CtaBand />
    </>
  );
}

export function locationHead(content: LocatieContent) {
  const path = `/timmerman-${content.slug}`;
  const geo = content.geo ?? nap.geo;
  const napAddress = {
    "@type": "PostalAddress",
    streetAddress: nap.straat,
    postalCode: nap.postcode,
    addressLocality: nap.plaats,
    addressRegion: "Zuid-Holland",
    addressCountry: nap.land,
  };
  const localBusiness = {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    "@id": `${SITE_URL}/#business`,
    name: nap.naam,
    legalName: nap.juridischeNaam,
    url: `${SITE_URL}${path}`,
    telephone: nap.telefoon,
    email: nap.email,
    image: ogImageDefault,
    priceRange: "€€",
    address: napAddress,
    geo: { "@type": "GeoCoordinates", latitude: geo.lat, longitude: geo.lng },
    areaServed: [
      { "@type": "City", name: content.plaats },
      ...(content.nabijgelegen ?? []).map((plaats) => ({ "@type": "City", name: plaats })),
    ],
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "07:00",
        closes: "17:00",
      },
    ],
    identifier: { "@type": "PropertyValue", name: "KvK", value: nap.kvk },
  };

  return {
    meta: [
      { title: content.title },
      { name: "description", content: content.description },
      { property: "og:title", content: content.socialTitle },
      { property: "og:description", content: content.socialDescription },
      { property: "og:url", content: `${SITE_URL}${path}` },
      { name: "geo.placename", content: content.plaats },
      { name: "geo.position", content: `${geo.lat};${geo.lng}` },
      { name: "ICBM", content: `${geo.lat}, ${geo.lng}` },
      ...ogImageMeta(ogImageDefault, `Timmerman ${content.plaats} — Fedor Timmerwerken`),
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${path}` }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(localBusiness) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: `Timmerman ${content.plaats}`,
          description: content.description,
          serviceType: "Timmerwerk en verbouw",
          url: `${SITE_URL}${path}`,
          areaServed: { "@type": "City", name: content.plaats },
          provider: { "@id": `${SITE_URL}/#business` },
          ...(content.prijzen && content.prijzen.length > 0
            ? {
                hasOfferCatalog: {
                  "@type": "OfferCatalog",
                  name: `Prijsindicatie timmerwerk ${content.plaats}`,
                  itemListElement: content.prijzen.map((prijs) => ({
                    "@type": "Offer",
                    name: prijs.dienst,
                    description: `Prijsindicatie in ${content.plaats}: ${prijs.indicatie}`,
                    priceCurrency: "EUR",
                    ...(prijs.href ? { url: `${SITE_URL}${prijs.href}` } : {}),
                  })),
                },
              }
            : {}),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: `Timmerman ${content.plaats}`, item: `${SITE_URL}${path}` },
          ],
        }),
      },
    ],
  };
}
